import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tags, Plus, Pencil, Trash2, Loader2, Zap, Hand, Settings } from "lucide-react";
import { toast } from "sonner";
import { TagFormDialog } from "./TagFormDialog";
import { DeleteTagDialog } from "./DeleteTagDialog";
import { getTagColorClass, type UserTag } from "@/hooks/useUserTags";

const USER_TAGS_QUERY_KEY = "user-tags";
const USER_TAG_ASSIGNMENTS_QUERY_KEY = "user-tag-assignments";
const TAG_AUTO_RULES_QUERY_KEY = "tag-auto-rules-counts";

export function TagManagement() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<UserTag | null>(null);
  const [deleteTag, setDeleteTag] = useState<UserTag | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch all tags
  const { data: tags = [], isLoading } = useQuery({
    queryKey: [USER_TAGS_QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_tags")
        .select("*")
        .order("is_system", { ascending: false })
        .order("name");
      if (error) throw error;
      return data as UserTag[];
    },
  });

  // Fetch tag usage counts with pagination to handle >1000 rows
  const { data: tagUsageCounts = {} } = useQuery({
    queryKey: [USER_TAG_ASSIGNMENTS_QUERY_KEY, "counts"],
    queryFn: async () => {
      const allAssignments: { tag_id: string }[] = [];
      const pageSize = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("user_tag_assignments")
          .select("tag_id")
          .range(from, from + pageSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allAssignments.push(...data);
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      const counts: Record<string, number> = {};
      allAssignments.forEach((a) => {
        counts[a.tag_id] = (counts[a.tag_id] || 0) + 1;
      });
      return counts;
    },
  });

  // Fetch rule counts per tag
  const { data: ruleCountsPerTag = {} } = useQuery({
    queryKey: [TAG_AUTO_RULES_QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tag_auto_rules")
        .select("tag_id");
      if (error) throw error;
      
      const counts: Record<string, number> = {};
      data.forEach((r) => {
        counts[r.tag_id] = (counts[r.tag_id] || 0) + 1;
      });
      return counts;
    },
  });

  // Create tag mutation
  const createTagMutation = useMutation({
    mutationFn: async (data: { name: string; color: string; description: string; tag_type: 'dynamic' | 'manual' }) => {
      const response = await supabase.functions.invoke("admin-user-actions", {
        body: { action: "create_tag", data },
      });
      if (response.error) throw response.error;
      if (response.data?.error) throw new Error(response.data.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_TAGS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-logs"] });
      queryClient.invalidateQueries({ queryKey: [TAG_AUTO_RULES_QUERY_KEY] });
      toast.success("Tag created successfully");
      setFormOpen(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to create tag: ${error.message}`);
    },
  });

  // Update tag mutation
  const updateTagMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name: string; color: string; description: string; tag_type: 'dynamic' | 'manual' }) => {
      const response = await supabase.functions.invoke("admin-user-actions", {
        body: { action: "update_tag", data: { id, ...data } },
      });
      if (response.error) throw response.error;
      if (response.data?.error) throw new Error(response.data.error);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [USER_TAGS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_TAG_ASSIGNMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-logs"] });
      queryClient.invalidateQueries({ queryKey: [TAG_AUTO_RULES_QUERY_KEY] });
      toast.success("Tag updated successfully");
      // Don't auto-close for dynamic tags - user may still be editing filters
      if (variables.tag_type !== 'dynamic') {
        setFormOpen(false);
        setEditingTag(null);
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to update tag: ${error.message}`);
    },
  });

  // Delete tag mutation
  const deleteTagMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await supabase.functions.invoke("admin-user-actions", {
        body: { action: "delete_tag", data: { id } },
      });
      if (response.error) throw response.error;
      if (response.data?.error) throw new Error(response.data.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_TAGS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_TAG_ASSIGNMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-logs"] });
      queryClient.invalidateQueries({ queryKey: [TAG_AUTO_RULES_QUERY_KEY] });
      toast.success("Tag deleted successfully");
      setDeleteTag(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete tag: ${error.message}`);
    },
  });

  const handleOpenCreate = () => {
    setEditingTag(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (tag: UserTag) => {
    setEditingTag(tag);
    setFormOpen(true);
  };


  const handleSubmit = async (data: { name: string; color: string; description: string; tag_type: 'dynamic' | 'manual' }) => {
    setIsSubmitting(true);
    try {
      if (editingTag) {
        await updateTagMutation.mutateAsync({ id: editingTag.id, ...data });
      } else {
        await createTagMutation.mutateAsync(data);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTag) return;
    setIsDeleting(true);
    try {
      await deleteTagMutation.mutateAsync(deleteTag.id);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tags className="h-5 w-5" />
              Tag Management
            </CardTitle>
            <CardDescription>
              Create and manage tags to organize and segment users
            </CardDescription>
          </div>
          <Button onClick={handleOpenCreate} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Create Tag
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tags.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No tags created yet. Click "Create Tag" to add one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="h-9">
                  <TableHead>Tag</TableHead>
                  <TableHead className="hidden md:table-cell">Description</TableHead>
                  <TableHead className="w-[80px]">Type</TableHead>
                  <TableHead className="w-[50px] text-center">Filters</TableHead>
                  <TableHead className="w-[50px] text-right">Users</TableHead>
                  <TableHead className="w-[80px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tags.map((tag) => (
                  <TableRow key={tag.id} className="h-9">
                    <TableCell className="py-1">
                      <Badge className={getTagColorClass(tag.color)}>
                        {tag.name}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-1 hidden md:table-cell text-muted-foreground text-sm truncate max-w-[200px]">
                      {tag.description || "—"}
                    </TableCell>
                    <TableCell className="py-1">
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-xs gap-1",
                          tag.is_system
                            ? "border-violet-500/50 text-violet-700 bg-violet-500/10"
                            : tag.tag_type === 'dynamic' 
                              ? "border-amber-500/50 text-amber-700 bg-amber-500/10" 
                              : "border-blue-500/50 text-blue-700 bg-blue-500/10"
                        )}
                      >
                        {tag.is_system ? (
                          <><Settings className="h-3 w-3" /> System</>
                        ) : tag.tag_type === 'dynamic' ? (
                          <><Zap className="h-3 w-3" /> Dynamic</>
                        ) : (
                          <><Hand className="h-3 w-3" /> Manual</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-1 text-center tabular-nums text-sm">
                      {tag.tag_type === 'dynamic' ? (ruleCountsPerTag[tag.id] || 0) : "—"}
                    </TableCell>
                    <TableCell className="py-1 text-right tabular-nums">
                      {tagUsageCounts[tag.id] || 0}
                    </TableCell>
                    <TableCell className="py-1 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleOpenEdit(tag)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => setDeleteTag(tag)}
                          disabled={tag.is_system}
                          title={tag.is_system ? "System tags cannot be deleted" : "Delete tag"}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TagFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingTag(null);
        }}
        tag={editingTag}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />

      <DeleteTagDialog
        open={!!deleteTag}
        onOpenChange={(open) => !open && setDeleteTag(null)}
        tag={deleteTag}
        userCount={deleteTag ? tagUsageCounts[deleteTag.id] || 0 : 0}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />

    </>
  );
}
